/**
 * Normalizes an untrusted override map — the persisted payload on hydration
 * and the `overrides` slice of an imported settings bundle run through this
 * same function, so storage and transfer can never disagree about what a valid
 * override is (plan §3 "Settings transfer").
 *
 * Rules (plan D2 + §3 store line):
 *  - a non-object map → no overrides;
 *  - `null` → dropped (it is never written; absence already means "inherit");
 *  - `[]` → kept: it is the explicit "unbound" instruction;
 *  - registry ids: each chord is canonicalized with `normalizeChord`, which
 *    drops unparsable and typing-shaped chords; duplicates collapse. An
 *    override whose chords were ALL unreadable is dropped (the command inherits
 *    its scheme chords) rather than turned into an accidental unbinding;
 *  - ids this build does not know (another app version) are KEPT and reported
 *    in `unknownOverrideIds`. Their chords are inert here, so they are kept as
 *    bounded strings (canonicalized when this build can parse them) for the
 *    version that owns them;
 *  - keys that are not command-id shaped (`__proto__`, garbage) are dropped;
 *  - unknown ids whose name matches the settings-transfer guard's denied
 *    patterns (`token`, `secret`, … — `findDeniedKeyPattern`) are DROPPED and
 *    counted in `deniedIdCount`: kept, such an id would make every settings
 *    export and import fail the guard (`assertNoDeniedData`).
 *
 * Reserved chords and collisions are NOT handled here — `validateKeymap` owns
 * them and returns a durable `overridesPatch` plus notices (D6).
 */
import { normalizeChord } from '../model/chord';
import { defaultKeybindings } from '../model/defaultKeybindings';
import type { KeybindingCommandId, KeybindingScheme, SchemeId } from '../model/keybindingTypes';
import { findDeniedKeyPattern, isDeniedStoreKey } from '../../settings-transfer/model/settingsBundleManifest';
import type { KeybindingOverrideMap } from './keybindingsStoreTypes';

const COMMAND_ID_SHAPE = /^[a-z][A-Za-z0-9_-]*(?:\.[A-Za-z0-9_-]+)+$/;
const MAX_COMMAND_ID_LENGTH = 80;
const MAX_CHORD_LENGTH = 64;
/** Shared by hydration/import and the store actions, so neither can hold more than the other keeps. */
export const MAX_CHORDS_PER_COMMAND = 16;
const MAX_UNKNOWN_IDS = 100;

export type NormalizedOverrides = {
  readonly overrides: KeybindingOverrideMap;
  readonly unknownOverrideIds: readonly string[];
  /** Entries dropped as unreadable (bad keys, non-array values, bad chords). */
  readonly droppedCount: number;
  /** Unknown ids dropped because their name matches a settings-transfer denied pattern. */
  readonly deniedIdCount: number;
};

export function isKnownCommandId(id: string): id is KeybindingCommandId {
  return Object.prototype.hasOwnProperty.call(defaultKeybindings, id);
}

/** An id the settings-transfer guard would reject as a credential-looking key. */
export function isTransferDeniedId(id: string): boolean {
  return isDeniedStoreKey(id) || findDeniedKeyPattern(id) !== undefined;
}

export function unknownOverrideIdsOf(overrides: KeybindingOverrideMap): string[] {
  return Object.keys(overrides).filter((id) => !isKnownCommandId(id));
}

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Canonical, de-duplicated chords of a registry command; unreadable ones dropped. */
export function normalizeCommandChords(chords: readonly unknown[]): { chords: string[]; dropped: number } {
  const kept = new Set<string>();
  let dropped = 0;
  for (const entry of chords) {
    const chord = typeof entry === 'string' ? normalizeChord(entry) : null;
    if (chord === null || kept.size >= MAX_CHORDS_PER_COMMAND) dropped += 1;
    else kept.add(chord);
  }
  return { chords: [...kept], dropped };
}

function normalizeUnknownChords(chords: readonly unknown[]): { chords: string[]; dropped: number } {
  const kept = new Set<string>();
  let dropped = 0;
  for (const entry of chords) {
    const trimmed = typeof entry === 'string' ? entry.trim() : '';
    if (!trimmed || trimmed.length > MAX_CHORD_LENGTH || kept.size >= MAX_CHORDS_PER_COMMAND) {
      dropped += 1;
      continue;
    }
    kept.add(normalizeChord(trimmed) ?? trimmed);
  }
  return { chords: [...kept], dropped };
}

export function normalizeOverrides(value: unknown): NormalizedOverrides {
  if (!isPlainRecord(value)) return { overrides: {}, unknownOverrideIds: [], droppedCount: 0, deniedIdCount: 0 };
  const overrides: Record<string, readonly string[]> = {};
  const unknownOverrideIds: string[] = [];
  let droppedCount = 0;
  let deniedIdCount = 0;

  for (const [id, raw] of Object.entries(value)) {
    if (raw === null) continue;
    const known = isKnownCommandId(id);
    if (!known && isTransferDeniedId(id)) {
      deniedIdCount += 1;
      continue;
    }
    const validKey = known || (
      id.length <= MAX_COMMAND_ID_LENGTH && COMMAND_ID_SHAPE.test(id) &&
      unknownOverrideIds.length < MAX_UNKNOWN_IDS
    );
    if (!validKey || !Array.isArray(raw)) {
      droppedCount += 1;
      continue;
    }
    const { chords, dropped } = known ? normalizeCommandChords(raw) : normalizeUnknownChords(raw);
    droppedCount += dropped;
    // Every chord unreadable: inherit instead of silently unbinding.
    if (raw.length > 0 && chords.length === 0) continue;
    overrides[id] = chords;
    if (!known) unknownOverrideIds.push(id);
  }
  return { overrides, unknownOverrideIds, droppedCount, deniedIdCount };
}

/** An unknown or unavailable scheme id falls back to the Jam default. */
export function normalizeSchemeId(
  value: unknown,
  schemes: Readonly<Partial<Record<SchemeId, KeybindingScheme>>>,
): SchemeId {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(schemes, value)
    ? (value as SchemeId)
    : 'jam';
}
